import React from 'react';
import { safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';

interface AllBrandsTooltipProps {
  point: any;
  allSeries: any[];
  theme: any;
  tooltipCategoryTitle?: string;
  categoryName: string;
  formatMetric: (value: number, metricType: METRICTYPE, options?: any) => string;
  metricTypeToDisplay: METRICTYPE;
}

/**
 * This component is used to display the tooltip for the filterable stacked top entities chart.
 * It displays the percentage of the total value for each series based on the brands selected.
 */
const AllBrandsTooltip = ({ point, allSeries, theme, formatMetric, metricTypeToDisplay }: AllBrandsTooltipProps) => {
  const pointIndex = point?.index;

  const textStyle = {
    fontSize: '14px',
    color: theme.colors.primary,
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.08px'
  };

  const total = allSeries.reduce((sum, series) => {
    return sum + (series?.data?.[pointIndex]?.rawValue || 0);
  }, 0);

  const sortedAllSeries = allSeries.sort((a, b) => {
    return b.data[pointIndex]?.rawValue - a.data[pointIndex]?.rawValue;
  });

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '4px',
        border: `1px solid ${theme.colors.primaryGray}`,
        padding: '12px',
        width: '240px'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', ...textStyle }}>
        {sortedAllSeries.map((series) => {
          const rawValue = series.data[pointIndex]?.rawValue || 0;

          const percentValue = safeDivide(rawValue, total) * 100;

          const formattedPercentage = `${percentValue.toFixed(2)}%`;
          const formattedRawValue = formatMetric(rawValue, metricTypeToDisplay || METRICTYPE.VOLUME, {
            showFullValue: false,
            decimalPlaces: 1
          });

          const displayValue = `${formattedPercentage} (${formattedRawValue})`;
          return (
            <div key={series.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '3px',
                    height: '12px',
                    marginRight: '8px',
                    backgroundColor: series.color,
                    borderRadius: '4px'
                  }}
                />
                <span
                  style={{
                    maxWidth: '90px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }}
                >
                  {series.name}
                </span>
              </div>
              <span>{displayValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AllBrandsTooltip;
